import React, { useState } from "react";
import { Crosshair, Layers, Loader2, Maximize2, Minimize2, Minus, Pause, Play, Plus, Satellite } from "lucide-react";
import { BASEMAPS, LEGENDS, PRODUCTS, SATELLITES, formatScan } from "./radarConfig";

const chip = "mono flex h-8 items-center justify-center gap-1.5 border border-white/15 bg-[rgba(2,6,16,0.85)] px-2.5 text-[10px] font-bold uppercase tracking-widest text-slate-200 backdrop-blur-md transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400";
const active = "border-cyan-300/60 bg-cyan-500/20 text-white";

export const RadarToolbar = ({
  product, setProduct, satellite, setSatellite, overlays, setOverlays, radarOpacity, setRadarOpacity,
  sites, site, siteOverride, setSiteOverride, locate, locating, toggleFullscreen, isFullscreen, mapRef, basemap, setBasemap,
}) => {
  const [open, setOpen] = useState(null);
  const toggleMenu = (name) => setOpen((current) => (current === name ? null : name));
  const toggleOverlay = (key) => setOverlays((current) => ({ ...current, [key]: !current[key] }));

  return (
    <div className="pointer-events-none absolute inset-y-2 right-2 z-[420] flex flex-col items-end justify-between" data-testid="radar-toolbar">
      <div className="pointer-events-auto flex flex-col items-end gap-1.5">
        <div className="flex border border-white/15 bg-[rgba(2,6,16,0.85)] backdrop-blur-md" role="group" aria-label="Radar product">
          {PRODUCTS.map((item) => (
            <button
              key={item.id}
              type="button"
              data-testid={`radar-product-${item.id}`}
              title={item.label}
              onClick={() => { setProduct(item.id); setOpen(null); }}
              className={`mono h-8 border-r border-white/10 px-2.5 text-[10px] font-bold uppercase tracking-widest last:border-r-0 ${product.id === item.id ? "bg-cyan-500/25 text-white" : "text-slate-300 hover:bg-white/10"}`}
            >
              {item.short}
            </button>
          ))}
        </div>

        <div className="relative">
          <button type="button" data-testid="radar-satellite-button" onClick={() => toggleMenu("satellite")} className={`${chip} ${satellite !== "none" ? active : ""}`} title="Satellite imagery">
            <Satellite className="h-3.5 w-3.5" /> Sat{satellite !== "none" ? ` · ${SATELLITES.find((item) => item.id === satellite)?.label}` : ""}
          </button>
          {open === "satellite" && (
            <div className="absolute right-0 top-9 w-44 border border-white/15 bg-[rgba(2,6,16,0.95)] p-1 backdrop-blur-md" data-testid="radar-satellite-menu">
              {SATELLITES.map((item) => (
                <button key={item.id} type="button" data-testid={`radar-satellite-${item.id}`} onClick={() => { setSatellite(item.id); setOpen(null); }} className={`mono flex w-full items-center justify-between px-2.5 py-1.5 text-left text-[10px] font-bold uppercase tracking-widest hover:bg-white/10 ${satellite === item.id ? "text-cyan-300" : "text-slate-200"}`}>
                  {item.label}{item.id !== "none" && <span className="text-[8px] text-slate-500">GOES</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="relative">
          <button type="button" data-testid="radar-layers-button" onClick={() => toggleMenu("layers")} className={`${chip} ${open === "layers" ? active : ""}`} title="Layers">
            <Layers className="h-3.5 w-3.5" /> Layers
          </button>
          {open === "layers" && (
            <div className="absolute right-0 top-9 w-56 border border-white/15 bg-[rgba(2,6,16,0.95)] p-2 backdrop-blur-md" data-testid="radar-layers-menu">
              <div className="mb-1 flex border border-white/10">
                {Object.entries(BASEMAPS).map(([key, item]) => (
                  <button key={key} type="button" data-testid={`radar-basemap-${key}`} onClick={() => setBasemap(key)} className={`mono h-7 flex-1 text-[9px] font-bold uppercase tracking-widest ${basemap === key ? "bg-cyan-500/25 text-white" : "text-slate-300 hover:bg-white/10"}`}>{item.label}</button>
                ))}
              </div>
              {[["warnings", "NWS warnings"], ["labels", "Cities & roads"], ["sites", "Radar sites"], ["reports", "Storm reports (3h)"]].map(([key, label]) => (
                <label key={key} className="mono flex cursor-pointer items-center justify-between px-1 py-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-200 hover:text-white">
                  {label}
                  <input type="checkbox" data-testid={`radar-layer-${key}`} checked={Boolean(overlays[key])} onChange={() => toggleOverlay(key)} className="h-3.5 w-3.5 accent-cyan-400" />
                </label>
              ))}
              <div className="mt-1 border-t border-white/10 px-1 pt-2">
                <div className="mono mb-1 flex justify-between text-[9px] uppercase tracking-widest text-slate-400"><span>Radar opacity</span><span>{Math.round(radarOpacity * 100)}%</span></div>
                <input type="range" min="0.2" max="1" step="0.05" value={radarOpacity} onChange={(event) => setRadarOpacity(Number(event.target.value))} data-testid="radar-opacity-slider" className="w-full accent-cyan-400" />
              </div>
              {product.site && (
                <div className="mt-1 border-t border-white/10 px-1 pt-2">
                  <div className="mono mb-1 text-[9px] uppercase tracking-widest text-slate-400">Radar site</div>
                  <select
                    data-testid="radar-site-select"
                    value={siteOverride || ""}
                    onChange={(event) => {
                      const value = event.target.value || null;
                      setSiteOverride(value);
                      const chosen = sites.find((item) => item.id === value);
                      if (chosen) mapRef.current?.flyTo([chosen.lat, chosen.lon], Math.max(mapRef.current.getZoom(), 7), { duration: 0.6 });
                    }}
                    className="mono w-full border border-white/15 bg-black/60 px-2 py-1 text-[10px] uppercase text-slate-100 focus:border-cyan-400 focus:outline-none"
                  >
                    <option value="">Auto · nearest ({site?.tile || "—"})</option>
                    {sites.map((item) => <option key={item.id} value={item.id}>{item.tile} · {item.name}, {item.state}</option>)}
                  </select>
                </div>
              )}
            </div>
          )}
        </div>

        <button type="button" data-testid="radar-locate-button" onClick={locate} className={chip} title="Center on my location">
          {locating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Crosshair className="h-3.5 w-3.5" />} Locate
        </button>
      </div>

      <div className="pointer-events-auto flex flex-col gap-1.5">
        <div className="flex flex-col border border-white/15 bg-[rgba(2,6,16,0.85)] backdrop-blur-md">
          <button type="button" data-testid="radar-zoom-in" aria-label="Zoom in" onClick={() => mapRef.current?.zoomIn()} className="grid h-8 w-8 place-items-center text-slate-200 hover:bg-white/10"><Plus className="h-4 w-4" /></button>
          <button type="button" data-testid="radar-zoom-out" aria-label="Zoom out" onClick={() => mapRef.current?.zoomOut()} className="grid h-8 w-8 place-items-center border-t border-white/10 text-slate-200 hover:bg-white/10"><Minus className="h-4 w-4" /></button>
        </div>
        <button type="button" data-testid="radar-toolbar-fullscreen" aria-label={isFullscreen ? "Exit fullscreen" : "Fullscreen"} onClick={toggleFullscreen} className={`${chip} h-8 w-8 px-0`}>
          {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
};

export const RadarTimeline = ({ frame, setFrame, frameCount, playing, setPlaying, scanTime }) => (
  <div className="pointer-events-auto absolute bottom-2 left-1/2 z-[420] flex w-[min(520px,calc(100%-180px))] -translate-x-1/2 items-center gap-2 border border-white/15 bg-[rgba(2,6,16,0.88)] px-2 py-1.5 backdrop-blur-md" data-testid="radar-timeline">
    <button type="button" data-testid="radar-play-button" aria-label={playing ? "Pause loop" : "Play loop"} onClick={() => setPlaying((value) => !value)} className="grid h-7 w-7 shrink-0 place-items-center bg-cyan-500/20 text-cyan-100 hover:bg-cyan-500/35">
      {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
    </button>
    <input
      type="range"
      min="0"
      max={frameCount - 1}
      value={frame}
      data-testid="radar-frame-slider"
      aria-label="Radar frame"
      onChange={(event) => { setPlaying(false); setFrame(Number(event.target.value)); }}
      className="sd-range min-w-0 flex-1 accent-cyan-400"
    />
    <span data-testid="radar-frame-time" className="mono shrink-0 text-[10px] font-bold text-cyan-100">{scanTime}</span>
  </div>
);

export const RadarLegend = ({ product }) => {
  const stops = LEGENDS[product.legend];
  return (
    <div className="pointer-events-none absolute bottom-2 left-2 z-[420] border border-white/15 bg-[rgba(2,6,16,0.85)] px-2 py-1.5 backdrop-blur-md" data-testid="radar-legend">
      <div className="mono mb-1 text-[9px] font-bold uppercase tracking-widest text-slate-300">{product.label} · {product.unit}</div>
      <div className="flex h-2 w-[180px] sm:w-[240px]">
        {stops.map((stop) => <span key={stop.v} className="flex-1" style={{ background: stop.c }} />)}
      </div>
      <div className="mono mt-0.5 flex justify-between text-[8px] text-slate-400">
        <span>{stops[0].v}</span><span>{stops[Math.floor(stops.length / 2)].v}</span><span>{stops[stops.length - 1].v}</span>
      </div>
    </div>
  );
};

export const scanLabel = (scans, frame, frameCount, stepMinutes) => {
  const seq = frameCount - 1 - frame;
  const fromList = scans[scans.length - 1 - seq];
  if (fromList) return formatScan(fromList);
  return formatScan(new Date(Date.now() - seq * stepMinutes * 60000).toISOString());
};
